import { useQuery } from "@apollo/client";
import { getRouteApi } from "@tanstack/react-router";
import { Copy, Info } from "lucide-react";
import { useState } from "react";
import { Tooltip } from "@/components/Tooltip";
import { Heading3 } from "@/components/Typography";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { graphql, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils/common";
import { formatDateRangeBoundary } from "@/lib/utils/dates";
import { getErrorMessage } from "@/lib/utils/errors";
import { formatInteger, formatMetricValue } from "@/lib/utils/formatting";

type MetricData = {
  id: (typeof METRIC_DEFINITIONS)[number]["key"];
  title: (typeof METRIC_DEFINITIONS)[number]["label"];
  metric: string;
  subMetric?: string;
  tooltipDescription: string;
};

export type SupplyChainLotsData = NonNullable<ResultOf<typeof weightedAveragesQuery>["supplyChainLots"]>;
export type MetricKey = keyof SupplyChainLotsData;

export const METRIC_DEFINITIONS = [
  { key: "numSupplyChainLots", label: "Number of Lots" },
  { key: "mortalityRateMetric", label: "Mortality Rate" },
  { key: "averageDailyGainMetric", label: "Average Daily Gain" },
  { key: "feedConversionMetric", label: "Feed Conversion" },
  { key: "dryMatterIntakeMetric", label: "Dry Matter Intake" },
  { key: "costOfGainMetric", label: "Cost of Gain" },
  { key: "profitPerHeadMetric", label: "Profit Per Head" },
  { key: "daysOnFeedMetric", label: "Days on Feed" },
  { key: "weightAverageInMetric", label: "Weight Average In" },
  { key: "weightAverageOutMetric", label: "Weight Average Out" },
  { key: "primeFractionMetric", label: "Prime" },
  { key: "choiceFractionMetric", label: "Choice" },
  { key: "selectFractionMetric", label: "Select" },
] as const satisfies Array<{ key: MetricKey; label: string }>;

const routeApi = getRouteApi("/(authed)/_layout/benchmarks");

export function WeightedAverages() {
  const {
    sexClass,
    animalBreedIds,
    dateClosedAfter,
    dateClosedBefore,
    dateOpenedAfter,
    dateOpenedBefore,
    feedyardIds,
    lotDestinationIds,
    lotSourceIds,
    sourceStateCodes,
    weightAverageInMin,
    weightAverageInMax,
    destinationCities,
    destinationStateCodes,
    sourceCities,
    lotId,
  } = routeApi.useSearch();
  const { toast } = useToast();
  const [copying, setCopying] = useState(false);

  const { activeBusinessUnitId } = useActiveBusinessUnit();

  const { data, loading } = useQuery(weightedAveragesQuery, {
    variables: {
      sexClass: sexClass?.length ? sexClass : undefined,
      animalBreeds: animalBreedIds?.length ? animalBreedIds : undefined,
      dateOpenedAfter: formatDateRangeBoundary.start(dateOpenedAfter),
      dateOpenedBefore: formatDateRangeBoundary.end(dateOpenedBefore),
      dateClosedAfter: formatDateRangeBoundary.start(dateClosedAfter),
      dateClosedBefore: formatDateRangeBoundary.end(dateClosedBefore),
      destinationPartners: lotDestinationIds?.length ? lotDestinationIds : undefined,
      destinationCities: destinationCities?.length ? destinationCities : undefined,
      destinationStates: destinationStateCodes?.length ? destinationStateCodes : undefined,
      sourcePartners: lotSourceIds?.length ? lotSourceIds : undefined,
      sourceCities: sourceCities?.length ? sourceCities : undefined,
      sourceStates: sourceStateCodes?.length ? sourceStateCodes : undefined,
      feedyardPartners: feedyardIds?.length ? feedyardIds : undefined,
      status: ["CLOSED"],
      weightAverageInMin: weightAverageInMin || undefined,
      weightAverageInMax: weightAverageInMax || undefined,
      businessUnitIds: activeBusinessUnitId ? [activeBusinessUnitId] : undefined,
      referenceSearch: lotId || undefined,
    },
  });

  const handleCopy = async () => {
    setCopying(true);

    try {
      if (!data?.supplyChainLots) {
        toast({
          variant: "destructive",
          title: "Failed to copy metrics.",
          description: "No data available.",
        });
        return;
      }

      const csvData = transformMetricsToCSV(data.supplyChainLots);

      await navigator.clipboard.writeText(csvData);
      toast({
        variant: "default",
        title: "Success!",
        description: "Weighted averages copied to clipboard.",
      });
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Failed to copy metrics.",
        description: getErrorMessage(error) || "Something went wrong.",
      });
    } finally {
      setCopying(false);
    }
  };

  const metrics: MetricData[] = [
    {
      id: "mortalityRateMetric",
      title: "Mortality Rate",
      metric: formatMetricValue({
        value: data?.supplyChainLots?.mortalityRateMetric?.weightedAverage,
        metricType: "percentage",
      }),
      subMetric: formatSubMetric(data?.supplyChainLots?.mortalityRateMetric?.numAnimals),
      tooltipDescription: "The percentage of animals that died during the production cycle.",
    },
    {
      id: "averageDailyGainMetric",
      title: "Average Daily Gain",
      metric: formatMetricValue({
        value: data?.supplyChainLots?.averageDailyGainMetric?.weightedAverage,
        metricType: "weight",
      }),
      subMetric: formatSubMetric(data?.supplyChainLots?.averageDailyGainMetric?.numAnimals),
      tooltipDescription: "The average daily weight gain of the animal.",
    },
    {
      id: "feedConversionMetric",
      title: "Feed Conversion",
      metric: formatMetricValue({ value: data?.supplyChainLots?.feedConversionMetric?.weightedAverage }),
      subMetric: formatSubMetric(data?.supplyChainLots?.feedConversionMetric?.numAnimals),
      tooltipDescription: "The amount of feed required to produce a unit of weight gain.",
    },
    {
      id: "dryMatterIntakeMetric",
      title: "Dry Matter Intake",
      metric: formatMetricValue({
        value: data?.supplyChainLots?.dryMatterIntakeMetric?.weightedAverage,
        metricType: "weight",
      }),
      subMetric: formatSubMetric(data?.supplyChainLots?.dryMatterIntakeMetric?.numAnimals),
      tooltipDescription: "The amount of dry matter intake of the animal.",
    },
    {
      id: "costOfGainMetric",
      title: "Cost of Gain",
      metric: formatMetricValue({
        value: data?.supplyChainLots?.costOfGainMetric?.weightedAverage,
        metricType: "currency",
      }),
      subMetric: formatSubMetric(data?.supplyChainLots?.costOfGainMetric?.numAnimals),
      tooltipDescription: "The cost of the feed required to produce a unit of weight gain.",
    },
    {
      id: "profitPerHeadMetric",
      title: "Profit Per Head",
      metric: formatMetricValue({
        value: data?.supplyChainLots?.profitPerHeadMetric?.weightedAverage,
        metricType: "currency",
      }),
      subMetric: formatSubMetric(data?.supplyChainLots?.profitPerHeadMetric?.numAnimals),
      tooltipDescription: "The profit per head of the animal.",
    },
    {
      id: "daysOnFeedMetric",
      title: "Days on Feed",
      metric: formatMetricValue({
        value: data?.supplyChainLots?.daysOnFeedMetric?.weightedAverage,
        metricType: "days",
      }),
      subMetric: formatSubMetric(data?.supplyChainLots?.daysOnFeedMetric?.numAnimals),
      tooltipDescription: "The number of days on feed of the animal.",
    },
    {
      id: "weightAverageInMetric",
      title: "Weight Average In",
      metric: formatMetricValue({
        value: data?.supplyChainLots?.weightAverageInMetric?.weightedAverage,
        metricType: "weight",
      }),
      subMetric: formatSubMetric(data?.supplyChainLots?.weightAverageInMetric?.numAnimals),
      tooltipDescription: "The average weight of the animal at the start of the production cycle.",
    },
    {
      id: "weightAverageOutMetric",
      title: "Weight Average Out",
      metric: formatMetricValue({
        value: data?.supplyChainLots?.weightAverageOutMetric?.weightedAverage,
        metricType: "weight",
      }),
      subMetric: formatSubMetric(data?.supplyChainLots?.weightAverageOutMetric?.numAnimals),
      tooltipDescription: "The average weight of the animal at the end of the production cycle.",
    },
    {
      id: "primeFractionMetric",
      title: "Prime",
      metric: formatMetricValue({
        value: data?.supplyChainLots?.primeFractionMetric?.weightedAverage,
        metricType: "percentage",
      }),
      subMetric: formatSubMetric(data?.supplyChainLots?.primeFractionMetric?.numAnimals),
      tooltipDescription: "The percentage of animals that are considered prime.",
    },
    {
      id: "choiceFractionMetric",
      title: "Choice",
      metric: formatMetricValue({
        value: data?.supplyChainLots?.choiceFractionMetric?.weightedAverage,
        metricType: "percentage",
      }),
      subMetric: formatSubMetric(data?.supplyChainLots?.choiceFractionMetric?.numAnimals),
      tooltipDescription: "The percentage of animals that are considered choice.",
    },
    {
      id: "selectFractionMetric",
      title: "Select",
      metric: formatMetricValue({
        value: data?.supplyChainLots?.selectFractionMetric?.weightedAverage,
        metricType: "percentage",
      }),
      subMetric: formatSubMetric(data?.supplyChainLots?.selectFractionMetric?.numAnimals),
      tooltipDescription: "The percentage of animals that are considered select.",
    },
  ];

  const isCopyDisabled =
    copying || loading || !data || !data?.supplyChainLots || data?.supplyChainLots?.numSupplyChainLots === 0;

  return (
    <section className="px-4">
      <div className="flex justify-between items-center">
        <Heading3 className="text-base">Weighted Averages</Heading3>
        <Button onClick={handleCopy} disabled={isCopyDisabled} variant={"outline"}>
          <Copy />
          Copy Averages
        </Button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {loading
          ? Array.from({ length: metrics.length }).map((_, i) => <MetricCardSkeleton key={i} />)
          : metrics.map(({ id, title, tooltipDescription, metric, subMetric }) => {
              return (
                <MetricCard
                  testId={`metric-card-${id}`}
                  key={id}
                  title={title}
                  metric={metric}
                  subMetric={subMetric}
                  ariaLabel={title}
                  tooltip={
                    tooltipDescription && (
                      <Tooltip label={tooltipDescription}>
                        <Info className="ml-1 h-4 w-4 text-foreground" />
                      </Tooltip>
                    )
                  }
                />
              );
            })}
      </div>
    </section>
  );
}

function MetricCard({
  title,
  metric,
  tooltip,
  ariaLabel,
  testId,
  subMetric,
}: {
  title: React.ReactNode;
  metric: React.ReactNode;
  subMetric?: React.ReactNode;
  tooltip?: React.ReactNode;
  ariaLabel: string;
  testId?: string;
}) {
  return (
    <dl
      className="flex h-24 min-w-[10rem] max-w-[12rem] flex-1 flex-col gap-1 rounded-lg bg-secondary p-3"
      aria-label={ariaLabel}
      data-testid={testId}
    >
      <dt className="text-sm text-muted-foreground truncate">{title}</dt>
      <div className="flex justify-between">
        <dd className="font-bold truncate">{metric}</dd>
        {tooltip}
      </div>
      {subMetric ? <dd className="text-muted-foreground text-xs truncate">{subMetric}</dd> : null}
    </dl>
  );
}

const SKELETON_SIZES = {
  title: { height: "h-4", width: "w-20" },
  metric: { height: "h-6", width: "w-16" },
  subMetric: { height: "h-4", width: "w-16" },
  icon: { height: "h-4", width: "w-4" },
} as const;

function MetricCardSkeleton() {
  return (
    <MetricCard
      title={<Skeleton className={cn(SKELETON_SIZES.title.height, SKELETON_SIZES.title.width)} />}
      metric={<Skeleton className={cn(SKELETON_SIZES.metric.height, SKELETON_SIZES.metric.width)} />}
      tooltip={<Skeleton className={cn(SKELETON_SIZES.icon.height, SKELETON_SIZES.icon.width, "rounded-full")} />}
      subMetric={
        <Skeleton className={cn(SKELETON_SIZES.subMetric.height, SKELETON_SIZES.subMetric.width, "rounded-full")} />
      }
      ariaLabel="Loading metric"
    />
  );
}

export const formatSubMetric = (numAnimals: Maybe<number>) =>
  numAnimals != null ? `# ${formatInteger(numAnimals)} animals` : undefined;

export const weightedAveragesQuery = graphql(`
  query WeightedAveragesQuery(
    $sexClass: [SupplyChainSexClassEnumType]
    $animalBreeds: [UUID]
    $dateOpenedBefore: DateTime
    $dateOpenedAfter: DateTime
    $dateClosedBefore: DateTime
    $dateClosedAfter: DateTime
    $destinationPartners: [UUID!]
    $sourcePartners: [UUID!]
    $sourceStates: [String]
    $sourceCities: [String!]
    $destinationStates: [String!]
    $destinationCities: [String!]
    $feedyardPartners: [UUID!]
    $status: [SupplyChainStatusEnumType]
    $weightAverageInMin: Decimal
    $weightAverageInMax: Decimal
    $businessUnitIds: [ID]
    $referenceSearch: String
  ) {
    supplyChainLots(
      sexClass: $sexClass
      animalBreeds: $animalBreeds
      dateOpenedBefore: $dateOpenedBefore
      dateOpenedAfter: $dateOpenedAfter
      dateClosedBefore: $dateClosedBefore
      dateClosedAfter: $dateClosedAfter
      destinationPartners: $destinationPartners
      sourcePartners: $sourcePartners
      sourceStates: $sourceStates
      sourceCities: $sourceCities
      destinationStates: $destinationStates
      destinationCities: $destinationCities
      feedyardPartners: $feedyardPartners
      referenceSearch: $referenceSearch
      status: $status
      weightAverageInMin: $weightAverageInMin
      weightAverageInMax: $weightAverageInMax
      owners: $businessUnitIds
    ) {
      numSupplyChainLots
      averageDailyGainMetric {
        numAnimals
        weightedAverage
      }
      costOfGainMetric {
        numAnimals
        weightedAverage
      }
      mortalityRateMetric {
        numAnimals
        weightedAverage
      }
      primeFractionMetric {
        numAnimals
        weightedAverage
      }
      feedConversionMetric {
        numAnimals
        weightedAverage
      }
      selectFractionMetric {
        numAnimals
        weightedAverage
      }
      choiceFractionMetric {
        numAnimals
        weightedAverage
      }
      dryMatterIntakeMetric {
        numAnimals
        weightedAverage
      }
      daysOnFeedMetric {
        numAnimals
        weightedAverage
      }
      weightAverageInMetric {
        numAnimals
        weightedAverage
      }
      weightAverageOutMetric {
        numAnimals
        weightedAverage
      }
      profitPerHeadMetric {
        numAnimals
        weightedAverage
      }
    }
  }
`);

export const transformMetricsToCSV = (data: SupplyChainLotsData) => {
  const TAB_SEPARATOR = "\t";
  const NEW_LINE = "\n";

  const createRow = (rowTitle: string, values: string[]) => `${rowTitle}${TAB_SEPARATOR}${values.join(TAB_SEPARATOR)}`;

  const headers = METRIC_DEFINITIONS.map(({ label }) => label);

  const values = METRIC_DEFINITIONS.map(({ key }) => {
    if (key === "numSupplyChainLots") {
      return data[key]?.toString() ?? "";
    }
    return data[key]?.weightedAverage?.toString() ?? "";
  });

  const subMetrics = METRIC_DEFINITIONS.map(({ key }) => {
    if (key === "numSupplyChainLots") {
      return "";
    }
    return data[key]?.numAnimals?.toString() ?? "";
  });

  return [createRow("", headers), createRow("Value", values), createRow("# Animals", subMetrics)].join(NEW_LINE);
};
